import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, BACKEND_URL } from "../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
  Sparkles, Save, Download, ArrowLeft, Plus, Trash2, Edit3, Check,
  Clock, Utensils, Coffee, ChefHat, Users, Truck, ShoppingCart, Brush,
  ListChecks, FileText, RefreshCw, Calendar,
} from "lucide-react";
import PageHeader from "../../components/PageHeader";
import AddToCalendarButton from "../../components/AddToCalendarButton";

const SECCIONES = [
  { key: "itinerario", label: "Itinerario", icon: Clock, color: "blue", fields: ["hora", "actividad", "responsable", "notas"], primaryField: "actividad" },
  { key: "montaje", label: "Montaje", icon: Utensils, color: "amber", fields: ["tarea", "responsable", "hora", "notas"], primaryField: "tarea" },
  { key: "cocina", label: "Cocina", icon: ChefHat, color: "emerald", fields: ["platillo", "cantidad", "hora_salida", "responsable", "notas"], primaryField: "platillo" },
  { key: "servicio", label: "Servicio", icon: Users, color: "purple", fields: ["tarea", "responsable", "hora", "notas"], primaryField: "tarea" },
  { key: "barra", label: "Barra", icon: Coffee, color: "amber", fields: ["tarea", "responsable", "notas"], primaryField: "tarea" },
  { key: "personal", label: "Personal", icon: Users, color: "stone", fields: ["nombre", "rol", "hora_entrada", "hora_salida", "notas"], primaryField: "rol" },
  { key: "proveedores", label: "Proveedores", icon: Truck, color: "stone", fields: ["nombre", "servicio", "contacto", "notas"], primaryField: "nombre" },
  { key: "compras", label: "Compras", icon: ShoppingCart, color: "stone", fields: ["producto", "cantidad", "notas"], primaryField: "producto" },
  { key: "limpieza", label: "Limpieza", icon: Brush, color: "stone", fields: ["tarea", "responsable", "hora", "notas"], primaryField: "tarea" },
];

const TONE = {
  blue: "bg-blue-50 border-blue-200 text-blue-800",
  amber: "bg-amber-50 border-amber-200 text-amber-800",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  purple: "bg-purple-50 border-purple-200 text-purple-800",
  stone: "bg-stone-50 border-stone-200 text-stone-800",
};

const HojaOperativa = () => {
  const { eventoId } = useParams();
  const navigate = useNavigate();
  const [sheet, setSheet] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [editingRows, setEditingRows] = useState([]);

  // Asistente
  const [asistenteOpen, setAsistenteOpen] = useState(false);
  const [asistenteText, setAsistenteText] = useState("");
  const [asistenteLoading, setAsistenteLoading] = useState(false);
  const [propuesta, setPropuesta] = useState(null);

  const fetchSheet = useCallback(async () => {
    try {
      // Get or create
      await axios.post(`${API}/eventos/${eventoId}/hoja-operativa`);
      const r = await axios.get(`${API}/eventos/${eventoId}/hoja-operativa`);
      setSheet(r.data.sheet);
      setSummary(r.data.summary);
    } catch (e) {
      toast.error("Error al cargar hoja operativa");
    } finally {
      setLoading(false);
    }
  }, [eventoId]);

  useEffect(() => { fetchSheet(); }, [fetchSheet]);

  const generarConAsistente = async () => {
    if (asistenteText.trim().length < 10) {
      toast.error("Describe el evento con mas detalle.");
      return;
    }
    setAsistenteLoading(true);
    try {
      const r = await axios.post(`${API}/eventos/${eventoId}/hoja-operativa/asistente`, { texto: asistenteText });
      setPropuesta(r.data.propuesta);
      toast.success("Propuesta generada. Revisa y aplica.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar");
    } finally {
      setAsistenteLoading(false);
    }
  };

  const aplicarPropuesta = async () => {
    if (!propuesta) return;
    try {
      const r = await axios.post(`${API}/eventos/${eventoId}/hoja-operativa/aplicar-propuesta`, {
        propuesta, texto_original: asistenteText,
      });
      setSheet(r.data.sheet);
      toast.success("Hoja operativa aplicada");
      setAsistenteOpen(false);
      setPropuesta(null);
      setAsistenteText("");
    } catch { toast.error("Error al aplicar"); }
  };

  const saveSection = async (key, rows) => {
    setSaving(true);
    try {
      await axios.put(`${API}/operational-sheets/${sheet.sheet_id}`, { [key]: rows });
      setSheet((s) => ({ ...s, [key]: rows }));
      toast.success("Guardado");
      setEditingSection(null);
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const openEdit = (sec) => {
    setEditingSection(sec);
    setEditingRows([...(sheet[sec.key] || [])]);
  };

  const addRow = (sec) => {
    const empty = sec.fields.reduce((acc, f) => ({ ...acc, [f]: "" }), {});
    empty._rid = `r_${Date.now()}_${Math.random().toString(36).slice(2,7)}`;
    setEditingRows((rs) => [...rs, empty]);
  };

  const updRow = (idx, field, val) => {
    setEditingRows((rs) => rs.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
  };

  const delRow = (idx) => setEditingRows((rs) => rs.filter((_, i) => i !== idx));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" /></div>;

  if (!sheet) return <div className="p-6 text-stone-500">No se pudo cargar.</div>;

  return (
    <div className="space-y-5 pb-24" data-testid="hoja-operativa-page">
      <PageHeader
        title="Hoja Operativa"
        description={summary?.evento ? `Orden de trabajo del equipo - ${summary.evento.nombre || ""} - ${summary.evento.fecha || ""}` : "Orden de trabajo del equipo."}
        primaryAction={{
          label: "Asistente AI",
          icon: Sparkles,
          testId: "btn-abrir-asistente",
          onClick: () => setAsistenteOpen(true),
        }}
        secondaryActions={[
          { label: "Volver al evento", icon: ArrowLeft, testId: "btn-volver-evento", onClick: () => navigate(`/panel/eventos/${eventoId}`) },
          { label: "PDF interno", icon: Download, testId: "btn-pdf-interno", onClick: () => window.open(`${BACKEND_URL}/api/eventos/${eventoId}/hoja-operativa/pdf-interno`, "_blank") },
          { label: "PDF cliente", icon: FileText, testId: "btn-pdf-cliente", onClick: () => window.open(`${BACKEND_URL}/api/eventos/${eventoId}/hoja-operativa/pdf-cliente`, "_blank") },
        ]}
      />

      {/* Boton calendario destacado */}
      <div className="flex justify-end">
        <AddToCalendarButton
          eventoId={eventoId}
          evento={summary?.evento}
          variant="outline"
          className="border-blue-300 text-blue-700 hover:bg-blue-50 h-11"
          testId="btn-calendario-hoja"
        />
      </div>

      {/* Resumen del evento como tarjeta superior */}
      {summary?.evento && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <h3 className="font-display text-lg text-emerald-900 mb-2">Resumen del evento</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
              <div><span className="text-emerald-700 text-xs">Cliente:</span><br /><span className="font-medium text-emerald-900">{summary.evento.cliente_nombre || "—"}</span></div>
              <div><span className="text-emerald-700 text-xs">Fecha:</span><br /><span className="font-medium text-emerald-900">{summary.evento.fecha || "—"}</span></div>
              <div><span className="text-emerald-700 text-xs">Horario:</span><br /><span className="font-medium text-emerald-900">{summary.evento.hora_inicio || ""} - {summary.evento.hora_fin || ""}</span></div>
              <div><span className="text-emerald-700 text-xs">Invitados:</span><br /><span className="font-medium text-emerald-900">{(summary.evento.adultos || 0) + (summary.evento.ninos || 0)} ({summary.evento.adultos || 0} adultos, {summary.evento.ninos || 0} ninos)</span></div>
              {summary.evento.tipo && <div><span className="text-emerald-700 text-xs">Tipo:</span><br /><span className="font-medium text-emerald-900 capitalize">{summary.evento.tipo}</span></div>}
              {summary.evento.locacion && <div className="sm:col-span-2"><span className="text-emerald-700 text-xs">Lugar:</span><br /><span className="font-medium text-emerald-900">{summary.evento.locacion}</span></div>}
            </div>
          </CardContent>
        </Card>
      )}

      {sheet.resumen_libre && (
        <Card className="border-stone-200">
          <CardContent className="p-3">
            <p className="text-xs text-stone-500 mb-1">Descripcion del usuario (asistente):</p>
            <p className="text-sm text-stone-700 italic">{sheet.resumen_libre}</p>
          </CardContent>
        </Card>
      )}

      {/* Secciones como tarjetas verticales (no tabs en celular) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {SECCIONES.map((sec) => {
          const rows = sheet[sec.key] || [];
          const Icon = sec.icon;
          return (
            <Card key={sec.key} className="border-stone-200" data-testid={`seccion-${sec.key}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center ${TONE[sec.color]}`}><Icon className="w-4 h-4" /></span>
                    <h3 className="font-display text-base text-stone-900">{sec.label}</h3>
                    <span className="text-xs text-stone-500">({rows.length})</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => openEdit(sec)} className="h-8" data-testid={`btn-edit-${sec.key}`}>
                    <Edit3 className="w-3 h-3 mr-1" /> Editar
                  </Button>
                </div>
                {rows.length === 0 ? (
                  <p className="text-xs text-stone-400 italic">Sin elementos. Usa el asistente o agrega manualmente.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {rows.slice(0, 6).map((r, i) => (
                      <li key={`${sec.key}-row-${i}`} className="text-sm text-stone-700 border-b border-stone-100 pb-1">
                        <span className="font-medium">{r[sec.primaryField] || r.tarea || r.actividad || "—"}</span>
                        {r.hora && <span className="text-xs text-stone-500 ml-2">{r.hora}</span>}
                        {r.responsable && <span className="text-xs text-stone-500 ml-2">· {r.responsable}</span>}
                        {r.cantidad && <span className="text-xs text-stone-500 ml-2">· {r.cantidad}</span>}
                      </li>
                    ))}
                    {rows.length > 6 && <li className="text-xs text-stone-400 italic">+ {rows.length - 6} mas...</li>}
                  </ul>
                )}
              </CardContent>
            </Card>
          );
        })}

        {/* Checklist */}
        <Card className="border-stone-200 lg:col-span-2" data-testid="seccion-checklist">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 flex items-center justify-center"><ListChecks className="w-4 h-4" /></span>
                <h3 className="font-display text-base text-stone-900">Checklist</h3>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              {["antes", "durante", "despues"].map((fase) => {
                const items = (sheet.checklist?.[fase] || []);
                return (
                  <div key={fase}>
                    <p className="font-medium text-stone-800 capitalize mb-1">{fase} del evento</p>
                    {items.length === 0 ? <p className="text-xs text-stone-400 italic">Sin items</p> : (
                      <ul className="space-y-1">
                        {items.map((it, i) => (
                          <li key={it._rid || `${fase}-${i}`} className="flex items-start gap-2 text-stone-700">
                            <input
                              type="checkbox"
                              checked={!!it.completado}
                              onChange={async (e) => {
                                const nuevo = { ...(sheet.checklist || {}) };
                                nuevo[fase] = items.map((x, idx) => idx === i ? { ...x, completado: e.target.checked } : x);
                                setSheet((s) => ({ ...s, checklist: nuevo }));
                                try { await axios.put(`${API}/operational-sheets/${sheet.sheet_id}`, { checklist: nuevo }); } catch { toast.error("Error"); }
                              }}
                              data-testid={`chk-${fase}-${i}`}
                              className="mt-1"
                            />
                            <span className={it.completado ? "line-through text-stone-400" : ""}>{it.texto}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Notas internas */}
        <Card className="border-amber-200 bg-amber-50 lg:col-span-2">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-amber-700" />
              <h3 className="font-display text-base text-amber-900">Notas internas (privadas)</h3>
            </div>
            <Textarea
              rows={3}
              value={sheet.notas_internas || ""}
              onChange={(e) => setSheet((s) => ({ ...s, notas_internas: e.target.value }))}
              onBlur={async () => { try { await axios.put(`${API}/operational-sheets/${sheet.sheet_id}`, { notas_internas: sheet.notas_internas || "" }); } catch { toast.error("Error"); } }}
              className="bg-white text-sm"
              data-testid="textarea-notas-internas"
            />
          </CardContent>
        </Card>
      </div>

      {/* Asistente Dialog */}
      <Dialog open={asistenteOpen} onOpenChange={setAsistenteOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-asistente">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-emerald-700" /> Cuentame como sera el evento</DialogTitle>
            <DialogDescription>
              Escribe en lenguaje natural lo operativo (horarios, menu, montaje, personal, actividades). El sistema generara la hoja operativa.
              Los datos del cliente, fecha, lugar e invitados ya estan capturados - no necesitas repetirlos.
            </DialogDescription>
          </DialogHeader>
          {!propuesta ? (
            <div className="space-y-3">
              <Textarea
                rows={10}
                value={asistenteText}
                onChange={(e) => setAsistenteText(e.target.value)}
                placeholder="Ej: Llegada 10am, desayuno 11am, pastel 1:30pm, cierre 3pm. Menu: chilaquiles, molletes, omelette. Montaje: mesa principal, area de regalos, barra. Personal: 2 meseros, 1 cocinera. Servir bebidas calientes y aguas frescas."
                className="text-sm"
                data-testid="textarea-asistente"
              />
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => { setAsistenteText(""); }} className="h-11">Limpiar</Button>
                <Button onClick={generarConAsistente} disabled={asistenteLoading} className="bg-emerald-900 hover:bg-emerald-800 h-11 px-6" data-testid="btn-generar-asistente">
                  {asistenteLoading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generando...</> : <><Sparkles className="w-4 h-4 mr-2" /> Generar hoja operativa</>}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <Card className="border-emerald-200 bg-emerald-50">
                <CardContent className="p-3 text-sm text-emerald-800">
                  El sistema genero esta hoja operativa. Revisa abajo y aplica para guardar.
                </CardContent>
              </Card>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {SECCIONES.map((sec) => {
                  const items = propuesta[sec.key] || [];
                  if (!items.length) return null;
                  return (
                    <div key={sec.key} className="bg-stone-50 p-2 rounded">
                      <p className="font-semibold text-sm text-stone-800 capitalize mb-1">{sec.label} ({items.length})</p>
                      <ul className="text-xs text-stone-600 space-y-0.5 ml-4">
                        {items.map((r, i) => <li key={r._rid || `${sec.key}-prop-${i}`}>· {r[sec.primaryField] || r.tarea || r.actividad || JSON.stringify(r)}</li>)}
                      </ul>
                    </div>
                  );
                })}
                {propuesta.notas_internas && (
                  <div className="bg-amber-50 p-2 rounded">
                    <p className="font-semibold text-sm text-amber-800 mb-1">Notas internas</p>
                    <p className="text-xs text-amber-700">{propuesta.notas_internas}</p>
                  </div>
                )}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setPropuesta(null)} className="h-11">Regenerar</Button>
                <Button onClick={() => setAsistenteOpen(false)} variant="outline" className="h-11">Cancelar</Button>
                <Button onClick={aplicarPropuesta} className="bg-emerald-900 hover:bg-emerald-800 h-11 px-6" data-testid="btn-aplicar-propuesta">
                  <Check className="w-4 h-4 mr-2" /> Aplicar y guardar
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Section editor dialog */}
      <Dialog open={!!editingSection} onOpenChange={(o) => !o && setEditingSection(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-edit-seccion">
          <DialogHeader>
            <DialogTitle>Editar {editingSection?.label}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {editingRows.map((row, idx) => (
              <Card key={row._rid || `edit-${idx}`} className="border-stone-200">
                <CardContent className="p-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <p className="text-xs font-semibold text-stone-600">Item #{idx + 1}</p>
                    <Button size="sm" variant="ghost" onClick={() => delRow(idx)} className="h-7"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {editingSection?.fields.map((f) => (
                      <div key={f}>
                        <Label className="text-xs capitalize">{f.replace("_", " ")}</Label>
                        <Input value={row[f] || ""} onChange={(e) => updRow(idx, f, e.target.value)} className="h-9" data-testid={`row-${idx}-${f}`} />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
            <Button variant="outline" onClick={() => addRow(editingSection)} className="w-full h-11 border-dashed" data-testid="btn-add-row"><Plus className="w-4 h-4 mr-2" /> Agregar item</Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingSection(null)} className="h-11">Cancelar</Button>
            <Button onClick={() => saveSection(editingSection.key, editingRows)} disabled={saving} className="bg-emerald-900 hover:bg-emerald-800 h-11" data-testid="btn-guardar-seccion">
              <Save className="w-4 h-4 mr-2" /> Guardar seccion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HojaOperativa;
